/**
 * Auth0 SPA config (domain, clientId, audience) per region and stack.
 *
 * Resolution in the app:
 * 1. Home-realm discovery (CloudFront KVS) returns `auth0TenantRegion` — "AU" | "US" | "EU".
 * 2. `getAuth0ConfigForRegion(region)` reads `AUTH0_TENANTS_DEV` or `AUTH0_TENANTS_PROD`
 *    from `REACT_APP_APP_ENV` (build-time dev vs prod stack).
 *
 * This module does **not** read `REACT_APP_AUTH0_DOMAIN` / `REACT_APP_AUTH0_*`. Those env vars
 * are only used in `index.js` when **no** `auth0TenantRegion` is present (fallback for hosts
 * not in the KVS map, local dev, etc.). Keep them aligned with the matching row here when
 * you set them on a deployment so fallback matches regional behaviour.
 *
 * US dev values are fully defined here (same pattern as prod US and AU dev) — not injected
 * per deploy via env for regional login.
 *
 * Note: Auth0 tenant region is independent of API region. A customer may authenticate via
 * the AU Auth0 tenant but have their API hosted in US.
 */

/** Production Auth0 SPA config per region (used when `REACT_APP_APP_ENV` is not `dev`). */
export const AUTH0_TENANTS_PROD = {
  AU: {
    domain: "id.orchestrated.io",
    clientId: "suYwzcyl0pErk3cYZGQ9SjSevqGeAouz",
    audience: "https://orchestrated-integration.au.auth0.com/api/v2/",
  },
  US: {
    domain: "id.us.teamform.co",
    clientId: "A3xeBBHrbgexDrPvG1n8YudqeUrkTgmX",
    audience: "https://teamform-us.us.auth0.com/api/v2/",
  },
  EU: {
    domain: "id.eu.teamform.co",
    clientId: null, // TODO: Register TeamForm App UI SPA in teamform-eu Auth0 tenant
    audience: null, // TODO: Set once SPA is registered
  },
};

/** Development Auth0 SPA config per region (used when `REACT_APP_APP_ENV` is `dev`). */
export const AUTH0_TENANTS_DEV = {
  AU: {
    domain: "id-test.orchestrated.io",
    clientId: "AmYOynNNXcwvouJGxCBhRzlYFZylJYS3",
    audience: "https://orchestrated-test.au.auth0.com/api/v2/",
  },
  // TeamForm App UI DEV — keep in sync with teamform-auth0 tenant.dev.us.yaml
  US: {
    domain: "id.us.dev.teamform.co",
    clientId: "A1cu37KxTM1Sj0zeP3GhTqhbOPJauhpl",
    audience: "https://teamform-us-dev.us.auth0.com/api/v2/",
  },
  EU: {
    domain: "id.eu.dev.teamform.co",
    clientId: null, // TODO: Register TeamForm App UI SPA in teamform-eu Auth0 tenant
    audience: null, // TODO: Set once SPA is registered
  },
};

const DEFAULT_REGION = "AU";

const isDevStack = () => process.env.REACT_APP_APP_ENV === "dev";

export const PROD_AUTH0_DOMAINS = Object.values(AUTH0_TENANTS_PROD)
  .map((t) => t.domain)
  .filter(Boolean);

/**
 * Returns Auth0 config for the given region, falling back to AU.
 * @param {string | null | undefined} region - "AU", "US", or "EU"
 * @returns {{ domain: string, clientId: string, audience: string }}
 */
export const getAuth0ConfigForRegion = (region) => {
  const map = isDevStack() ? AUTH0_TENANTS_DEV : AUTH0_TENANTS_PROD;

  const tenant = map[region];
  if (tenant && tenant.domain && tenant.clientId && tenant.audience) {
    return tenant;
  }
  return map[DEFAULT_REGION];
};

/**
 * @deprecated Prefer {@link getAuth0ConfigForRegion} for runtime config (respects dev vs prod).
 * For the raw production map only, use the named export {@link AUTH0_TENANTS_PROD}.
 * This default export is an alias of `AUTH0_TENANTS_PROD`.
 */
export default AUTH0_TENANTS_PROD;
